import { requestIdleCallback } from '@theme/utilities';

/*
 * Declarative shadow DOM is only initialized on the initial render of the page.
 * If the component is mounted after the browser finishes the initial render,
 * the shadow root needs to be manually hydrated.
 */
export class DeclarativeShadowElement extends HTMLElement {
  connectedCallback() {
    if (!this.shadowRoot) {
      const template = this.querySelector(':scope > template[shadowrootmode="open"]');

      if (!(template instanceof HTMLTemplateElement)) return;

      const shadow = this.attachShadow({ mode: 'open' });
      shadow.append(template.content.cloneNode(true));
    }
  }
}

/**
 * @typedef {Record<string, Element | Element[] | undefined>} Refs
 */

/**
 * @template {Refs} T
 * @typedef {T & Refs} RefsType
 */

/**
 * Base class that powers our custom web components.
 *
 * Manages references to child elements with `ref` attributes and sets up mutation observers to keep
 * the refs updated when the DOM changes. Also handles declarative event listeners using.
 *
 * @template {Refs} [T=Refs]
 * @extends {DeclarativeShadowElement}
 */
export class Component extends DeclarativeShadowElement {
  /**
   * An object holding references to child elements with `ref` attributes.
   *
   * @type {RefsType<T>}
   */
  refs = /** @type {RefsType<T>} */ ({});

  /**
   * An array of required refs. If a ref is not found, an error will be thrown.
   *
   * @type {string[] | undefined}
   */
  requiredRefs;

  /**
   * Gets the root node of the component, which is either its shadow root or the component itself.
   *
   * @returns {(ShadowRoot | Component<T>)[]} The root nodes.
   */
  get roots() {
    return this.shadowRoot ? [this, this.shadowRoot] : [this];
  }

  /**
   * Called when the element is connected to the document's DOM.
   *
   * Initializes event listeners and refs.
   */
  connectedCallback() {
    super.connectedCallback();
    registerEventListeners();

    this.#updateRefs();

    requestIdleCallback(() => {
      for (const root of this.roots) {
        this.#mutationObserver.observe(root, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['ref'],
          attributeOldValue: true,
        });
      }
    });
  }

  /**
   * Called when the element is re-rendered by the Section Rendering API.
   */
  updatedCallback() {
    this.#mutationObserver.takeRecords();
    this.#updateRefs();
  }

  /**
   * Called when the element is disconnected from the document's DOM.
   *
   * Disconnects the mutation observer.
   */
  disconnectedCallback() {
    this.#mutationObserver.disconnect();
  }

  /**
   * Updates the `refs` object by querying all descendant elements with `ref` attributes and storing references to them.
   *
   * This method is called to keep the `refs` object in sync with the DOM.
   */
  #updateRefs() {
    const refs = /** @type any */ ({});
    const elements = this.roots.reduce((acc, root) => {
      for (const element of root.querySelectorAll('[ref]')) {
        if (!this.#isDescendant(element)) continue;
        acc.add(element);
      }

      return acc;
    }, /** @type {Set<Element>} */ (new Set()));

    for (const ref of elements) {
      const refName = ref.getAttribute('ref') ?? '';
      const isArray = refName.endsWith('[]');
      const path = isArray ? refName.slice(0, -2) : refName;

      if (isArray) {
        const array = Array.isArray(refs[path]) ? refs[path] : [];

        array.push(ref);
        refs[path] = array;
      } else {
        refs[path] = ref;
      }
    }

    if (this.requiredRefs?.length) {
      for (const ref of this.requiredRefs) {
        if (!(ref in refs)) {
          throw new MissingRefError(ref, this);
        }
      }
    }

    this.refs = /** @type {RefsType<T>} */ (refs);
  }

  /**
   * MutationObserver instance to observe changes in the component's DOM subtree and update refs accordingly.
   *
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    if (
      mutations.some(
        (m) =>
          (m.type === 'attributes' && this.#isDescendant(m.target)) ||
          (m.type === 'childList' && [...m.addedNodes, ...m.removedNodes].some(this.#isDescendant))
      )
    ) {
      this.#updateRefs();
    }
  });

  /**
   * Checks if a given node is a descendant of this component.
   *
   * @param {Node} node - The node to check.
   * @returns {boolean} True if the node is a descendant of this component.
   */
  #isDescendant = (node) => getClosestComponent(getAncestor(node)) === this;
}

/**
 * Get the ancestor of a given node.
 *
 * @param {Node} node - The node to get the ancestor of.
 * @returns {Node | null} The ancestor of the node or null if none is found.
 */
function getAncestor(node) {
  if (node.parentNode) return node.parentNode;

  const root = node.getRootNode();
  if (root instanceof ShadowRoot) return root.host;

  return null;
}

/**
 * Recursively finds the closest ancestor that is an instance of `Component`.
 *
 * @param {Node | null} node - The starting node to search from.
 * @returns {HTMLElement | null} The closest ancestor `Component` instance or null if none is found.
 */
function getClosestComponent(node) {
  if (!node) return null;
  if (node instanceof Component) return node;
  if (node instanceof HTMLElement && node.tagName.toLowerCase().endsWith('-component')) return node;

  const ancestor = getAncestor(node);
  if (ancestor) return getClosestComponent(ancestor);

  return null;
}

/**
 * Initializes the event listeners for custom event handling.
 *
 * Sets up event listeners for specified events and delegates the handling of those events
 * to methods defined on the closest `Component` instance, based on custom attributes.
 */
let initialized = false;

function registerEventListeners() {
  if (initialized) return;
  initialized = true;

  const events = ['click', 'change', 'select', 'focus', 'blur', 'submit', 'input', 'keydown', 'keyup', 'toggle'];
  const shouldBubble = ['focus', 'blur'];
  const expensiveEvents = ['pointerenter', 'pointerleave'];

  for (const eventName of [...events, ...expensiveEvents]) {
    const attribute = `on:${eventName}`;

    document.addEventListener(
      eventName,
      (event) => {
        const element = getElement(event);

        if (!element) return;

        const proxiedEvent =
          event.target !== element
            ? new Proxy(event, {
                get(target, property) {
                  if (property === 'target') return element;

                  const value = Reflect.get(target, property);

                  if (typeof value === 'function') {
                    return value.bind(target);
                  }

                  return value;
                },
              })
            : event;

        const value = element.getAttribute(attribute) ?? '';
        let [selector, method] = value.split('/');
        // Extract the last segment of the attribute value delimited by `?` or `/`
        // Do not use lookback for Safari 16.0 compatibility
        const matches = value.match(/([\/\?][^\/\?]+)([\/\?][^\/\?]+)$/);
        const data = matches ? matches[2] : null;
        const instance = selector
          ? selector.startsWith('#')
            ? document.querySelector(selector)
            : element.closest(selector)
          : getClosestComponent(element);

        if (!(instance instanceof Component) || !method) return;

        method = method.replace(/\?.*/, '');

        const callback = /** @type {any} */ (instance)[method];

        if (typeof callback === 'function') {
          try {
            /** @type {(Event | Data)[]} */
            const args = [proxiedEvent];

            if (data) args.unshift(parseData(data));

            callback.call(instance, ...args);
          } catch (error) {
            console.error(error);
          }
        }
      },
      { capture: true }
    );
  }

  /** @param {Event} event */
  function getElement(event) {
    const target = event.composedPath?.()[0] ?? event.target;

    if (!(target instanceof Element)) return;

    if (target.hasAttribute(`on:${event.type}`)) {
      return target;
    }

    if (expensiveEvents.includes(event.type)) {
      return null;
    }

    return event.bubbles || shouldBubble.includes(event.type) ? target.closest(`[on\\:${event.type}]`) : null;
  }
}

/**
 * Parses a string to extract data based on a delimiter.
 *
 * @param {string} str - The string to parse.
 * @returns {Object|Array<string|number>|string} The parsed data.
 */
function parseData(str) {
  const delimiter = str[0];
  const data = str.slice(1);

  return delimiter === '?'
    ? Object.fromEntries(
        Array.from(new URLSearchParams(data).entries()).map(([key, value]) => [key, parseValue(value)])
      )
    : parseValue(data);
}

/**
 * @typedef {Object|Array<string|number>|string} Data
 */

/**
 * Parses a string value to its appropriate type.
 *
 * @param {string} str - The string to parse.
 * @returns {Data} The parsed value.
 */
function parseValue(str) {
  if (str === 'true') return true;
  if (str === 'false') return false;

  const maybeNumber = Number(str);
  if (!isNaN(maybeNumber) && str.trim() !== '') return maybeNumber;

  return str;
}

/**
 * Throws a formatted error when a required ref is not found in the component.
 */
class MissingRefError extends Error {
  /**
   * @param {string} ref
   * @param {Component} component
   */
  constructor(ref, component) {
    super(`Required ref "${ref}" not found in component ${component.tagName.toLowerCase()}`);
  }
}



/**
 * NISHARA CUSTOM JAVASCRIPT - BRAND EDITION
 * Interactive features with #A45C5C brand color integration
 */

(function() {
  'use strict';

  // Brand color
  const BRAND_COLOR = '#A45C5C';
  const BRAND_COLOR_DARK = '#8B4949';
  const BRAND_COLOR_LIGHT = '#D4A5A5';

  // ========================================
  // INITIALIZATION
  // ========================================

  document.addEventListener('DOMContentLoaded', function() {
    initStickyHeader();
    initScrollAnimations();
    initProductCardHovers();
    initMobileMenu();
    initNewsletterForm();
    initSmoothScrolling();
    initQuickView();
    addLoadingAnimation();
    initParallaxEffect();
  });

  // ========================================
  // PAGE LOADING ANIMATION
  // ========================================

  function addLoadingAnimation() {
    document.body.style.opacity = '0';
    
    window.addEventListener('load', function() {
      setTimeout(() => {
        document.body.style.transition = 'opacity 0.5s ease';
        document.body.style.opacity = '1';
      }, 100);
    });
  }

  // ========================================
  // STICKY HEADER WITH BRAND COLOR
  // ========================================

  function initStickyHeader() {
    const header = document.querySelector('.site-header');
    if (!header) return;

    let lastScrollTop = 0;
    const scrollThreshold = 50;

    window.addEventListener('scroll', debounce(function() {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      
      if (scrollTop > scrollThreshold) {
        header.classList.add('scrolled');
      } else {
        header.classList.remove('scrolled');
      }

      lastScrollTop = scrollTop;
    }, 10));
  }

  // ========================================
  // SCROLL ANIMATIONS
  // ========================================

  function initScrollAnimations() {
    const observerOptions = {
      root: null,
      rootMargin: '0px 0px -100px 0px',
      threshold: 0.15
    };

    const observer = new IntersectionObserver(function(entries) {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
          // Add brand color accent on appearance
          if (entry.target.classList.contains('feature-card')) {
            entry.target.style.borderTop = `3px solid ${BRAND_COLOR}`;
          }
        }
      });
    }, observerOptions);

    // Observe elements with fade-in animation
    const animatedElements = document.querySelectorAll('.fade-in-on-scroll');
    animatedElements.forEach(element => {
      observer.observe(element);
    });

    // Animate product cards on scroll
    const productCards = document.querySelectorAll('.product-card');
    productCards.forEach((card, index) => {
      observer.observe(card);
    });
  }

  // ========================================
  // PRODUCT CARD HOVER EFFECTS
  // ========================================

  function initProductCardHovers() {
    const productCards = document.querySelectorAll('.product-card');
    
    productCards.forEach(card => {
      const primaryImage = card.querySelector('.product-image');
      const secondaryImage = card.querySelector('.product-image-secondary');
      
      card.addEventListener('mouseenter', function() {
        // Add subtle brand color glow on hover
        this.style.boxShadow = `0 12px 40px rgba(164, 92, 92, 0.2)`;
      });

      card.addEventListener('mouseleave', function() {
        this.style.boxShadow = '';
      });
    });
  }

  // ========================================
  // MOBILE MENU WITH BRAND STYLING
  // ========================================

  function initMobileMenu() {
    const menuToggle = document.querySelector('.mobile-menu-toggle');
    const nav = document.querySelector('.main-nav');
    
    if (!menuToggle || !nav) return;

    menuToggle.addEventListener('click', function(e) {
      e.stopPropagation();
      this.classList.toggle('active');
      nav.classList.toggle('mobile-active');
      document.body.classList.toggle('menu-open');

      // Animate hamburger
      const spans = this.querySelectorAll('span');
      if (this.classList.contains('active')) {
        spans[0].style.transform = 'rotate(45deg) translateY(8px)';
        spans[1].style.opacity = '0';
        spans[2].style.transform = 'rotate(-45deg) translateY(-8px)';
      } else {
        spans[0].style.transform = '';
        spans[1].style.opacity = '1';
        spans[2].style.transform = '';
      }
    });

    // Style mobile menu with brand colors
    if (window.innerWidth <= 768) {
      nav.style.background = `linear-gradient(135deg, ${BRAND_COLOR} 0%, ${BRAND_COLOR_DARK} 100%)`;
    }

    // Close menu when clicking outside
    document.addEventListener('click', function(event) {
      if (!menuToggle.contains(event.target) && !nav.contains(event.target)) {
        menuToggle.classList.remove('active');
        nav.classList.remove('mobile-active');
        document.body.classList.remove('menu-open');
        
        const spans = menuToggle.querySelectorAll('span');
        spans[0].style.transform = '';
        spans[1].style.opacity = '1';
        spans[2].style.transform = '';
      }
    });

    // Close menu on window resize
    window.addEventListener('resize', debounce(function() {
      if (window.innerWidth > 768) {
        menuToggle.classList.remove('active');
        nav.classList.remove('mobile-active');
        document.body.classList.remove('menu-open');
      }
    }, 250));
  }

  // ========================================
  // NEWSLETTER FORM
  // ========================================

  function initNewsletterForm() {
    const form = document.querySelector('.newsletter-form');
    if (!form) return;

    form.addEventListener('submit', function(e) {
      e.preventDefault();
      
      const input = this.querySelector('.newsletter-input');
      const submitBtn = this.querySelector('.newsletter-submit');
      const email = input.value.trim();

      // Validate email
      if (!isValidEmail(email)) {
        showNotification('Please enter a valid email address', 'error');
        input.style.borderColor = '#ff6b6b';
        return;
      }

      input.style.borderColor = '';

      // Show loading state
      submitBtn.disabled = true;
      const originalText = submitBtn.textContent;
      submitBtn.innerHTML = 'Subscribing... <span class="spinner"></span>';

      // Simulate API call (replace with actual Shopify API)
      setTimeout(() => {
        showNotification('🎉 Thank you for subscribing to Nishara!', 'success');
        input.value = '';
        submitBtn.disabled = false;
        submitBtn.textContent = originalText;
        
        // Add confetti effect
        createConfetti();
      }, 1500);
    });

    // Add focus effect with brand color
    const input = form.querySelector('.newsletter-input');
    input.addEventListener('focus', function() {
      this.style.borderColor = 'white';
      this.style.boxShadow = '0 0 0 3px rgba(255, 255, 255, 0.2)';
    });

    input.addEventListener('blur', function() {
      this.style.boxShadow = '';
    });
  }

  function isValidEmail(email) {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  }

  // ========================================
  // SMOOTH SCROLLING
  // ========================================

  function initSmoothScrolling() {
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
      anchor.addEventListener('click', function(e) {
        const href = this.getAttribute('href');
        if (href === '#') return;
        
        e.preventDefault();
        const target = document.querySelector(href);
        
        if (target) {
          const headerOffset = 100;
          const elementPosition = target.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      });
    });
  }

  // ========================================
  // QUICK VIEW MODAL
  // ========================================

  function initQuickView() {
    const quickViewButtons = document.querySelectorAll('.quick-view-btn');
    
    quickViewButtons.forEach(button => {
      button.addEventListener('click', function(e) {
        e.preventDefault();
        const productId = this.dataset.productId;
        openQuickView(productId);
      });
    });
  }

  function openQuickView(productId) {
    // Quick view modal implementation
    console.log('Opening quick view for product:', productId);
    showNotification('Quick view feature coming soon!', 'info');
  }

  // ========================================
  // NOTIFICATION SYSTEM WITH BRAND COLORS
  // ========================================

  function showNotification(message, type = 'info') {
    const notification = document.createElement('div');
    notification.className = `notification notification-${type}`;
    notification.textContent = message;
    
    // Brand-colored notifications
    let bgColor;
    switch(type) {
      case 'success':
        bgColor = BRAND_COLOR;
        break;
      case 'error':
        bgColor = '#d32f2f';
        break;
      default:
        bgColor = BRAND_COLOR_DARK;
    }
    
    Object.assign(notification.style, {
      position: 'fixed',
      top: '20px',
      right: '20px',
      padding: '1.2rem 2rem',
      background: bgColor,
      color: 'white',
      borderRadius: '4px',
      boxShadow: '0 8px 32px rgba(164, 92, 92, 0.3)',
      zIndex: '10000',
      opacity: '0',
      transform: 'translateX(400px)',
      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
      fontFamily: 'Montserrat, sans-serif',
      fontSize: '0.95rem',
      fontWeight: '600',
      maxWidth: '350px',
      backdropFilter: 'blur(10px)'
    });

    document.body.appendChild(notification);

    // Animate in
    setTimeout(() => {
      notification.style.opacity = '1';
      notification.style.transform = 'translateX(0)';
    }, 10);

    // Remove after 4 seconds
    setTimeout(() => {
      notification.style.opacity = '0';
      notification.style.transform = 'translateX(400px)';
      setTimeout(() => {
        if (document.body.contains(notification)) {
          document.body.removeChild(notification);
        }
      }, 400);
    }, 4000);
  }

  // ========================================
  // ADD TO CART WITH ANIMATION
  // ========================================

  document.addEventListener('click', function(e) {
    if (e.target.classList.contains('add-to-cart-btn') || 
        e.target.closest('.add-to-cart-btn')) {
      
      const button = e.target.classList.contains('add-to-cart-btn') ? 
                     e.target : e.target.closest('.add-to-cart-btn');
      
      if (button.disabled) return;
      
      const productCard = button.closest('.product-card');
      const productName = productCard.querySelector('.product-title').textContent;
      const productImage = productCard.querySelector('.product-image');
      
      // Disable button
      button.disabled = true;
      const originalText = button.textContent;
      button.innerHTML = 'Adding... <span class="spinner"></span>';
      
      // Create flying cart animation
      if (productImage) {
        createFlyingImage(productImage);
      }
      
      // Simulate add to cart (replace with Shopify API)
      setTimeout(() => {
        button.innerHTML = '✓ Added!';
        button.style.background = `linear-gradient(135deg, #4caf50 0%, #388e3c 100%)`;
        
        showNotification(`${productName} added to cart! 🛒`, 'success');
        
        // Update cart count if exists
        updateCartCount(1);
        
        setTimeout(() => {
          button.textContent = originalText;
          button.style.background = '';
          button.disabled = false;
        }, 2000);
      }, 800);
    }
  });

  // ========================================
  // FLYING IMAGE ANIMATION
  // ========================================

  function createFlyingImage(sourceImage) {
    const cartIcon = document.querySelector('.header-icon[href*="cart"]');
    if (!cartIcon) return;

    const flyingImg = sourceImage.cloneNode(true);
    const rect = sourceImage.getBoundingClientRect();
    const cartRect = cartIcon.getBoundingClientRect();
    
    flyingImg.style.position = 'fixed';
    flyingImg.style.top = rect.top + 'px';
    flyingImg.style.left = rect.left + 'px';
    flyingImg.style.width = rect.width + 'px';
    flyingImg.style.height = rect.height + 'px';
    flyingImg.style.zIndex = '9999';
    flyingImg.style.transition = 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)';
    flyingImg.style.borderRadius = '8px';
    
    document.body.appendChild(flyingImg);
    
    setTimeout(() => {
      flyingImg.style.top = cartRect.top + 'px';
      flyingImg.style.left = cartRect.left + 'px';
      flyingImg.style.width = '30px';
      flyingImg.style.height = '30px';
      flyingImg.style.opacity = '0';
    }, 10);
    
    setTimeout(() => {
      document.body.removeChild(flyingImg);
    }, 900);
  }

  // ========================================
  // CART COUNT UPDATE
  // ========================================

  function updateCartCount(increment) {
    // Update cart count badge (create if doesn't exist)
    const cartIcon = document.querySelector('.header-icon[href*="cart"]');
    if (!cartIcon) return;

    let badge = cartIcon.querySelector('.cart-badge');
    
    if (!badge) {
      badge = document.createElement('span');
      badge.className = 'cart-badge';
      Object.assign(badge.style, {
        position: 'absolute',
        top: '-8px',
        right: '-8px',
        background: BRAND_COLOR,
        color: 'white',
        borderRadius: '50%',
        width: '20px',
        height: '20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '0.7rem',
        fontWeight: 'bold',
        boxShadow: '0 2px 8px rgba(164, 92, 92, 0.4)'
      });
      badge.textContent = '0';
      cartIcon.style.position = 'relative';
      cartIcon.appendChild(badge);
    }
    
    const currentCount = parseInt(badge.textContent) || 0;
    const newCount = currentCount + increment;
    badge.textContent = newCount;
    
    // Bounce animation
    badge.style.animation = 'none';
    setTimeout(() => {
      badge.style.animation = 'cartBounce 0.5s ease';
    }, 10);
  }

  // Add cart bounce animation
  const style = document.createElement('style');
  style.textContent = `
    @keyframes cartBounce {
      0%, 100% { transform: scale(1); }
      50% { transform: scale(1.3); }
    }
  `;
  document.head.appendChild(style);

  // ========================================
  // CONFETTI EFFECT
  // ========================================

  function createConfetti() {
    const colors = [BRAND_COLOR, BRAND_COLOR_LIGHT, '#C9A690', '#E8D5C4'];
    const confettiCount = 30;
    
    for (let i = 0; i < confettiCount; i++) {
      const confetti = document.createElement('div');
      const color = colors[Math.floor(Math.random() * colors.length)];
      
      Object.assign(confetti.style, {
        position: 'fixed',
        width: '10px',
        height: '10px',
        background: color,
        top: '50%',
        left: '50%',
        opacity: '1',
        borderRadius: Math.random() > 0.5 ? '50%' : '0',
        zIndex: '9999',
        pointerEvents: 'none'
      });
      
      document.body.appendChild(confetti);
      
      const angle = (Math.PI * 2 * i) / confettiCount;
      const velocity = 100 + Math.random() * 100;
      const lifetime = 1000 + Math.random() * 1000;
      
      const tx = Math.cos(angle) * velocity;
      const ty = Math.sin(angle) * velocity;
      
      confetti.animate([
        { transform: 'translate(0, 0) rotate(0deg)', opacity: 1 },
        { transform: `translate(${tx}px, ${ty}px) rotate(${Math.random() * 360}deg)`, opacity: 0 }
      ], {
        duration: lifetime,
        easing: 'cubic-bezier(0, 0.5, 0.5, 1)'
      });
      
      setTimeout(() => {
        document.body.removeChild(confetti);
      }, lifetime);
    }
  }

  // ========================================
  // PARALLAX EFFECT
  // ========================================

  function initParallaxEffect() {
    const parallaxElements = document.querySelectorAll('[data-parallax]');
    
    if (parallaxElements.length === 0) return;

    window.addEventListener('scroll', debounce(function() {
      const scrolled = window.pageYOffset;
      
      parallaxElements.forEach(element => {
        const speed = element.dataset.parallax || 0.5;
        const yPos = -(scrolled * speed);
        element.style.transform = `translateY(${yPos}px)`;
      });
    }, 10));
  }

  // ========================================
  // IMAGE LAZY LOADING
  // ========================================

  function initLazyLoading() {
    const imageObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const img = entry.target;
          img.src = img.dataset.src;
          img.classList.add('loaded');
          img.style.animation = 'fadeIn 0.5s ease';
          observer.unobserve(img);
        }
      });
    });

    const images = document.querySelectorAll('img[data-src]');
    images.forEach(img => imageObserver.observe(img));
  }

  // ========================================
  // UTILITY FUNCTIONS
  // ========================================

  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  // ========================================
  // SCROLL PROGRESS BAR
  // ========================================

  function createScrollProgress() {
    const progressBar = document.createElement('div');
    Object.assign(progressBar.style, {
      position: 'fixed',
      top: '0',
      left: '0',
      height: '3px',
      background: `linear-gradient(90deg, ${BRAND_COLOR} 0%, ${BRAND_COLOR_DARK} 100%)`,
      zIndex: '10001',
      transition: 'width 0.1s ease',
      boxShadow: `0 2px 10px rgba(164, 92, 92, 0.3)`
    });
    
    document.body.appendChild(progressBar);
    
    window.addEventListener('scroll', debounce(function() {
      const windowHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
      const scrolled = (window.pageYOffset / windowHeight) * 100;
      progressBar.style.width = scrolled + '%';
    }, 10));
  }

  // Initialize scroll progress bar
  createScrollProgress();

  // ========================================
  // EXPORT FOR TESTING
  // ========================================

  window.NisharaCustom = {
    showNotification,
    updateCartCount,
    createConfetti,
    BRAND_COLOR
  };

  // ========================================
  // CONSOLE BRANDING
  // ========================================

  console.log(
    '%c✨ Nishara Candles ✨',
    `color: ${BRAND_COLOR}; font-size: 24px; font-weight: bold; font-family: 'Cormorant Garamond', serif;`
  );
  console.log(
    '%cWebsite crafted with love 🕯️',
    `color: ${BRAND_COLOR_DARK}; font-size: 14px;`
  );

})();
